import { useState } from 'react';
import { connect } from 'react-redux';

import { siteboardAction } from '../actions/defaultActions';
import { 
  CREATE_STRIPE_PORTAL_SESSION, SET_UPGRADE_TO_STANDARD_DIALOGUE_OPEN, SET_EDIT_SUBSCRIPTION_MANAGERS_DIALOGUE_OPEN, getSubscriptionOwnerId,
  TRIAL_PROJECT_LIMIT, TRIAL_SCHEDULE_LIMIT, TRIAL_ADDITIONAL_USER_LIMIT, getSubscriptionActiveAdditionalUserCount, 
  SET_EDIT_PROJECT_DIALOGUE_ID, ADD_SUBSCRIPTION_ADMIN, SiteboardApiValidator, CREATE_STANDARD_SUBSCRIPTION_CHECKOUT, ADD_PROJECT, getSubscriptionMonthlyPriceString,
  getSubscriptionMonthlyPriceBreakdownString, ADDITIONAL_USER_DESCRIPTION,
} from '../actions/types';
import { millisDurationToString } from '../Util';
import { format } from 'date-fns';

import Tooltip from './Tooltip';
import PushButton from './PushButton';

// icon imports
import UpgradeIcon from '../icons/upgradeIcon';
import CrownIcon from '../icons/crownIcon';
import ClockIcon from '../icons/clockIcon';
import TeamIcon from '../icons/teamIcon';

// test cases
const testLotsOfProjects = false;
const testLotsOfUsers = false;

const SubscriptionCard = ({ _users, _userId, _projects, siteboardAction, subscription }) => {
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);

  const numberOfProjectsToShow = 5;
  const numberOfUsersToShow = 5;
  let subscriptionStatus = null;
  let isExpired = null;
  let monthlyPrice = null;
  let monthlyPriceBreakdown = null;

  const ownerId = getSubscriptionOwnerId(subscription);
  const ownerFirstName = _users[ownerId].firstName;
  const ownerLastName = _users[ownerId].lastName;
  const ownerEmail = _users[ownerId].email;
  const subscriptionRole = subscription.members[_userId].role;

  const projects = [];
  for(const projectId in _projects) {
    if(_projects[projectId].subscriptionId !== subscription.id) continue;
    projects.push(_projects[projectId]);
    if(testLotsOfProjects) {
      for(let i = 0; i < 10; i++) {
        projects.push(_projects[projectId]);
      }
    }
  }

  const members = [];
  let memberCount = 0;
  let pendingMemberCount = 0;
  for(const userId in subscription.members) {
    if(subscription.members[userId].role === 'Owner') continue;
    members.push({ pending: false, id: userId, email: _users[userId].email, ...subscription.members[userId]});
    memberCount++;
    if(testLotsOfUsers) {
      for(let i = 0; i < 10; i++) {
        members.push({ pending: false, email: _users[userId].email, ...subscription.members[userId]});
      }
    }
  }
  for(const email in subscription.pendingMembers) {
    members.push({ pending: true, email: email, ...subscription.pendingMembers[email]});
    pendingMemberCount++;
    if(testLotsOfUsers) {
      for(let i = 0; i < 10; i++) {
        members.push({ pending: true, email: email, ...subscription.pendingMembers[email]});
      }
    }
  }

  // expiration and price info for Owner
  if(subscriptionRole === 'Owner') { 
    const millisUntilExpiration = subscription.endTimestamp - new Date().getTime();
    //const millisUntilExpiration = -1000*60*60*24*2; // test expired
    isExpired = millisUntilExpiration <= 0;
    // TODO: button to renew subscription if expired
    if(subscription.type === 'Trial') {
      if(isExpired) {
        subscriptionStatus = 'Expired, ended ' + millisDurationToString(Math.abs(millisUntilExpiration)) + ' ago';
      } else {
        subscriptionStatus = 'Active, expires on ' + format(new Date(subscription.endTimestamp), 'MM/dd') + ' (' + millisDurationToString(Math.abs(millisUntilExpiration)) + ')'; 
      }
    } else if(subscription.type === 'Standard') {
      if(isExpired) {
        subscriptionStatus = 'Expired, ended ' + millisDurationToString(Math.abs(millisUntilExpiration)) + ' ago';
      } else {
        if(subscription.autoRenew) {
          subscriptionStatus = 'Active, auto-renews on ' + format(new Date(subscription.endTimestamp), 'MM/dd') + ' (' + millisDurationToString(Math.abs(millisUntilExpiration)) + ')';
          monthlyPrice = getSubscriptionMonthlyPriceString(subscription);
          monthlyPriceBreakdown = '( ' + getSubscriptionMonthlyPriceBreakdownString(subscription) + ' )';
        } else {
          subscriptionStatus = 'Cancelled, expires in ' + millisDurationToString(Math.abs(millisUntilExpiration));
        }
      }
    } else if(subscription.type === 'Complimentary') {
      subscriptionStatus = 'Free forever';
    }
  }

  return (
    <div className='flex flex-col gap-y-4 rounded-2xl bg-[#222] text-white shadow-lg p-4 py-6'>
      <div className='flex flex-col md:flex-row md:justify-between gap-4'>

        {/* Subscription Type */}
        {subscriptionRole === 'Owner' &&
          <div className='md:w-1/2 flex flex-col gap-1'>
            <div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium bg-[#f9a928] text-black`}>{subscription.type} Subscription</span>
            </div>
            {subscriptionStatus !== null && <div>{subscriptionStatus}</div> }
            {monthlyPrice !== null &&
              <div>{monthlyPrice} <span className='text-[#999]'>{monthlyPriceBreakdown}</span></div>
            }
          </div>
        }

        {/* User Info */}
        <div className='md:w-1/2 text-left'>
          <p className='text-[#bbb] text-sm leading-[1em] pb-2'>Account Owner</p> 
          <h2 className='flex items-center gap-1.5 text-xl font-bold'>{ownerFirstName} {ownerLastName}</h2>
          <p className='text-sm leading-[1em]'>{ownerEmail}</p>
        </div>
      </div>
          
      {/* Manage Billing */}
      {(subscription.type === 'Standard' && SiteboardApiValidator[CREATE_STRIPE_PORTAL_SESSION].isUserIdAuthorized(_userId, subscription, null)) &&
        <PushButton 
          textColor='white'
          bgColor='#471cff'
          hoverBgColor='#603aff'
          onClick={() => {
            siteboardAction(CREATE_STRIPE_PORTAL_SESSION, {}, (output) => {
              window.location.href = output.portalUrl;
            });
          }}
        >
          Manage Billing
        </PushButton>
      }

      {/* Trial Limits */}
      {(subscriptionRole === 'Owner' && subscription.type === 'Trial') &&
        <>
          <hr className='border-[#444]' />
          <div className='md:w-[50%]'>
            <div>
              <h3 className='text-lg font-semibold mb-2'>Trial Limits</h3>
              <div><p className='inline-block'>{subscription.projectCount} of {TRIAL_PROJECT_LIMIT}</p> projects used</div>
              <div><p className='inline-block'>{subscription.scheduleCount} of {TRIAL_SCHEDULE_LIMIT}</p> schedules used</div>
              <div><p className='inline-block'>{subscription.userCount-1} of {TRIAL_ADDITIONAL_USER_LIMIT}</p> additional users used</div>
            </div>
          </div>
        </>
      }

      {/* Upgrade to Standard Plan */}
      {(subscription.type === 'Trial' && SiteboardApiValidator[CREATE_STANDARD_SUBSCRIPTION_CHECKOUT].isUserIdAuthorized(_userId, subscription, null)) &&
        <PushButton 
          textColor='black'
          bgColor='#f9a928'
          hoverBgColor='#f79800'
          onClick={() => { siteboardAction(SET_UPGRADE_TO_STANDARD_DIALOGUE_OPEN, { isOpen: true }); }}
        >
          <div className='flex items-center gap-x-[6px]'>
            <UpgradeIcon color='#000' /> 
            <div>Upgrade to Standard Plan</div>
          </div>
        </PushButton>
      }

      <hr className='border-[#444]' />
      <div className='flex flex-col md:flex-row gap-4'>
        {/* Projects */}
        <div className='md:w-1/2 flex flex-col gap-2'>
          <h3 className='text-lg font-semibold'>
            {projects.length} Project{projects.length !== 1 && 's'}
          </h3>
          <ul className=''>
            {projects.map((project, index) => (index < numberOfProjectsToShow || showAllProjects) && (
              <li key={index} className='flex items-center text-gray-200'>
                <button 
                  className='flex items-center gap-2 w-full text-left hover:bg-[#444] px-2 py-1 rounded-lg'
                  onClick={() => {
                    siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: project.id });
                  }}
                >
                  <span className='w-2 min-w-2 h-2 min-h-2 rounded-full bg-[#fff]'></span>
                  <span>{project.name}</span>
                </button>
              </li>
            ))}
          </ul>
          {projects.length > numberOfProjectsToShow && 
            <button 
              className='text-blue-400 underline hover:text-blue-600 transition' 
              onClick={() => { setShowAllProjects(!showAllProjects); }}
            >
              Show {showAllProjects ? 'fewer projects' : (projects.length - numberOfProjectsToShow) + ' more projects'}
            </button>
          }
        </div>

        <hr className='md:hidden border-[#444]' />
        <div className='hidden md:block'>
          <div className='w-[1px] h-full bg-[#444]'></div>
        </div>

        {/* Users */}
        <div className='md:w-1/2 flex flex-col'>
          <h3 className='text-lg font-semibold'>
            <Tooltip tooltipContent={ADDITIONAL_USER_DESCRIPTION}>
              <div className='flex items-center gap-1.5 inline underline decoration-dotted underline-offset-4 cursor-help ml-1'>
                <TeamIcon TeamIcon size='1em' color='#f9a928' /> {memberCount} Additional User{memberCount !== 1 ? 's' : ''}
              </div>
            </Tooltip>
            <div className='inline-block'>{pendingMemberCount > 0 ? (', '+pendingMemberCount+' Pending') : ''}</div>
          </h3>
          <ul className=''>
            {members.map((member, index) => (index < numberOfUsersToShow || showAllUsers) && (
              <li key={index} className='flex items-center space-x-2 text-gray-200 px-2 py-1'>
                <span className='w-2 min-w-2 h-2 min-h-2 rounded-full bg-[#fff]'></span>
                <div className=''>{member.email}</div>
                {member.pending === true && <div title='Pending Invite'><ClockIcon size='1em' /></div>}
                {member.role === 'Admin' && <div title='Account Manager'><CrownIcon size='1em' /></div>}
                {member.id === _userId && <div className='font-bold text-[#f9a928]'>(you)</div>}
              </li>
            ))}
          </ul>
          {members.length > numberOfUsersToShow && 
            <button 
              className='text-blue-400 underline hover:text-blue-600' 
              onClick={() => { setShowAllUsers(!showAllUsers); }}
            >
              Show {showAllUsers ? 'fewer users' : (members.length - numberOfUsersToShow) + ' more users'}
            </button>
          }
          {SiteboardApiValidator[ADD_SUBSCRIPTION_ADMIN].isUserIdAuthorized(_userId, subscription, null) &&
            <div>
              <PushButton 
                textColor='white'
                bgColor='#471cff'
                hoverBgColor='#603aff'
                onClick={() => { siteboardAction(SET_EDIT_SUBSCRIPTION_MANAGERS_DIALOGUE_OPEN, { isOpen: true}); }}
              >
                Assign Account Managers
              </PushButton>
            </div>
          }
        </div>
      </div>

      {/* Is Account Manager */}
      {(subscriptionRole === 'Admin' && ownerId !== _userId) &&
        <>
          <hr className='border-[#444]' />
          <div><div className='inline-block mr-1'><CrownIcon size='1em' /></div> You're an Account Manager for this account and have authority to create, edit, and assign members to all projects. </div>
        </>
      }
    </div>
  );
};

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _projects: state.serverSynced.projects,
}};
export default connect(mapStateToProps, { siteboardAction })(SubscriptionCard);
